import { apiClient, type Paginated } from '@/core/api';
import type {
  AssignTestInput,
  AttemptAnswers,
  AttemptListDto,
  AttemptListParams,
  AttemptPupilDetailDto,
  AttemptStaffDetailDto,
  GradeAttemptInput,
  QuestionDto,
  QuestionWriteInput,
  TestAssignmentDto,
  TestDetailDto,
  TestListDto,
  TestListParams,
  TestWriteInput,
} from './types';

// ---------------------------------------------------------------------------
// Tests
// ---------------------------------------------------------------------------

/** `GET /api/tests/` — staff see all; pupils get the reduced payload limited to
 *  published tests assigned to their classes inside the availability window
 *  (ADR-013). Parents receive nothing (403). */
export async function getTests(params: TestListParams = {}): Promise<Paginated<TestListDto>> {
  const { data } = await apiClient.get<Paginated<TestListDto>>('/tests/', { params });
  return data;
}

/** `GET /api/tests/{public_id}/` — staff detail (questions + assignments). */
export async function getTest(publicId: string): Promise<TestDetailDto> {
  const { data } = await apiClient.get<TestDetailDto>(`/tests/${publicId}/`);
  return data;
}

/** `GET /api/tests/{public_id}/` as a pupil — no questions, no answer keys. */
export async function getPupilTest(publicId: string): Promise<TestListDto> {
  const { data } = await apiClient.get<TestListDto>(`/tests/${publicId}/`);
  return data;
}

/** `POST /api/tests/` — admin & teacher. */
export async function createTest(input: TestWriteInput): Promise<TestDetailDto> {
  const { data } = await apiClient.post<TestDetailDto>('/tests/', input);
  return data;
}

/** `PATCH /api/tests/{public_id}/` — settings only (`status` is not writable). */
export async function updateTest(publicId: string, input: TestWriteInput): Promise<TestDetailDto> {
  const { data } = await apiClient.patch<TestDetailDto>(`/tests/${publicId}/`, input);
  return data;
}

/** `DELETE /api/tests/{public_id}/` — archives (C4); attempts stay. */
export async function archiveTest(publicId: string): Promise<void> {
  await apiClient.delete(`/tests/${publicId}/`);
}

/** `POST /api/tests/{public_id}/publish/`. */
export async function publishTest(publicId: string): Promise<TestDetailDto> {
  const { data } = await apiClient.post<TestDetailDto>(`/tests/${publicId}/publish/`);
  return data;
}

/** `POST /api/tests/{public_id}/unpublish/`. */
export async function unpublishTest(publicId: string): Promise<TestDetailDto> {
  const { data } = await apiClient.post<TestDetailDto>(`/tests/${publicId}/unpublish/`);
  return data;
}

/** `POST /api/tests/{public_id}/duplicate/` — the edit path for tests frozen by
 *  attempts (ADR-013); returns the new draft copy. */
export async function duplicateTest(publicId: string): Promise<TestDetailDto> {
  const { data } = await apiClient.post<TestDetailDto>(`/tests/${publicId}/duplicate/`);
  return data;
}

/** `POST /api/tests/{public_id}/assign/` — give the test to a class. */
export async function assignTest(
  publicId: string,
  input: AssignTestInput
): Promise<TestAssignmentDto> {
  const { data } = await apiClient.post<TestAssignmentDto>(`/tests/${publicId}/assign/`, input);
  return data;
}

/** `POST /api/tests/{public_id}/unassign/` — hides the test from that class. */
export async function unassignTest(publicId: string, schoolClass: string): Promise<void> {
  await apiClient.post(`/tests/${publicId}/unassign/`, { school_class: schoolClass });
}

// ---------------------------------------------------------------------------
// Questions (staff-only — the payload carries answer keys)
// ---------------------------------------------------------------------------

/** `GET /api/test-questions/?test=` — ordered within the test. */
export async function getQuestions(testId: string): Promise<Paginated<QuestionDto>> {
  const { data } = await apiClient.get<Paginated<QuestionDto>>('/test-questions/', {
    params: { test: testId, page_size: 100 },
  });
  return data;
}

/** `POST /api/test-questions/` — `order` appends server-side. */
export async function createQuestion(input: QuestionWriteInput): Promise<QuestionDto> {
  const { data } = await apiClient.post<QuestionDto>('/test-questions/', input);
  return data;
}

/** `PATCH /api/test-questions/{public_id}/` — rejected once the test has attempts. */
export async function updateQuestion(
  publicId: string,
  input: Partial<QuestionWriteInput>
): Promise<QuestionDto> {
  const { data } = await apiClient.patch<QuestionDto>(`/test-questions/${publicId}/`, input);
  return data;
}

/** `DELETE /api/test-questions/{public_id}/`. */
export async function deleteQuestion(publicId: string): Promise<void> {
  await apiClient.delete(`/test-questions/${publicId}/`);
}

/** `POST /api/test-questions/{public_id}/reorder/` — 1-based target position. */
export async function reorderQuestion(publicId: string, order: number): Promise<QuestionDto> {
  const { data } = await apiClient.post<QuestionDto>(`/test-questions/${publicId}/reorder/`, {
    order,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Attempts
// ---------------------------------------------------------------------------

/** `GET /api/attempts/` — pupils get their own rows, staff the grading queue. */
export async function getAttempts(
  params: AttemptListParams = {}
): Promise<Paginated<AttemptListDto>> {
  const { data } = await apiClient.get<Paginated<AttemptListDto>>('/attempts/', { params });
  return data;
}

/** `GET /api/attempts/{public_id}/` as the owning pupil (stripped questions). */
export async function getPupilAttempt(publicId: string): Promise<AttemptPupilDetailDto> {
  const { data } = await apiClient.get<AttemptPupilDetailDto>(`/attempts/${publicId}/`);
  return data;
}

/** `GET /api/attempts/{public_id}/` as staff (answers + full questions). */
export async function getStaffAttempt(publicId: string): Promise<AttemptStaffDetailDto> {
  const { data } = await apiClient.get<AttemptStaffDetailDto>(`/attempts/${publicId}/`);
  return data;
}

/** `POST /api/attempts/` `{test}` — a pupil starts an attempt. Lifecycle
 *  rejections arrive as coded validation errors (`test_not_available`,
 *  `attempts_exhausted`, `attempt_in_progress`, `test_empty`). */
export async function startAttempt(testId: string): Promise<AttemptPupilDetailDto> {
  const { data } = await apiClient.post<AttemptPupilDetailDto>('/attempts/', { test: testId });
  return data;
}

/** `PATCH /api/attempts/{public_id}/answers/` — autosave a partial answer map
 *  (null clears one). Past the deadline the server force-submits and answers
 *  with `time_expired`. */
export async function saveAttemptAnswers(
  publicId: string,
  answers: AttemptAnswers
): Promise<AttemptPupilDetailDto> {
  const { data } = await apiClient.patch<AttemptPupilDetailDto>(`/attempts/${publicId}/answers/`, {
    answers,
  });
  return data;
}

/** `POST /api/attempts/{public_id}/submit/` — accepted even past the deadline
 *  (force-submit of the saved answers, ADR-013). */
export async function submitAttempt(publicId: string): Promise<AttemptPupilDetailDto> {
  const { data } = await apiClient.post<AttemptPupilDetailDto>(`/attempts/${publicId}/submit/`);
  return data;
}

/** `POST /api/attempts/{public_id}/grade/` — manual grading of open questions. */
export async function gradeAttempt(
  publicId: string,
  input: GradeAttemptInput
): Promise<AttemptStaffDetailDto> {
  const { data } = await apiClient.post<AttemptStaffDetailDto>(
    `/attempts/${publicId}/grade/`,
    input
  );
  return data;
}
